import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Pencil } from "lucide-react";
import { api } from "@/lib/api";
import { inr } from "@/theme";
import { useAdminTheme } from "@/admin/AdminTheme";
import { ACard, AButton, Badge, AdminPageTitle } from "@/admin/ui";
import { Spinner } from "@/components/common";
import { StarRating } from "@/components/StarRating";

export default function AdminProductDetail() {
  const { c } = useAdminTheme();
  const { id } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    api(`/admin/products/${id}`).then(setP).catch(() => setP(null));
    api(`/products/${id}/reviews`).then(setReviews).catch(() => {});
  }, [id]);

  if (!p) return <Spinner full />;

  return (
    <div className="fade-up" style={{ maxWidth: 900 }}>
      <AdminPageTitle title={p.name} subtitle={`${p.category}${p.subcategory ? " · " + p.subcategory : ""}`}
        action={<div style={{ display: "flex", gap: 10 }}>
          <AButton variant="outline" onClick={() => navigate("/admin/products")}>Back</AButton>
          <AButton data-testid="edit-product" onClick={() => navigate(`/admin/product/${id}/edit`)}><Pencil size={14} style={{ verticalAlign: "middle", marginRight: 6 }} />Edit</AButton>
        </div>} />

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px,1fr))", gap: 16 }}>
        <ACard>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {(p.images || []).map((im, i) => <img key={i} src={im} alt="" style={{ width: 120, height: 150, objectFit: "cover", borderRadius: 10, border: `1px solid ${c.border}` }} />)}
          </div>
        </ACard>
        <ACard>
          <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
            <span className="font-display" style={{ fontSize: 28, fontWeight: 700 }}>{inr(p.price)}</span>
            {p.original_price && <span style={{ color: c.mutedText, textDecoration: "line-through" }}>{inr(p.original_price)}</span>}
          </div>
          <div style={{ margin: "10px 0" }}><StarRating rating={p.average_rating} count={p.review_count} /></div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", margin: "10px 0" }}>
            <Badge tone={p.stock > 5 ? "success" : p.stock > 0 ? "warning" : "danger"}>{p.stock} in stock</Badge>
            {p.featured && <Badge tone="primary">Featured</Badge>}
            {p.collection && <Badge tone="info">{p.collection}</Badge>}
          </div>
          <p style={{ fontSize: 14, lineHeight: 1.6, color: c.onSurfaceSecondary }}>{p.description}</p>
          <div style={{ fontSize: 13, color: c.mutedText }}>{p.material} · {p.dimensions}</div>
          {p.variants?.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 11, letterSpacing: 0.6, textTransform: "uppercase", color: c.mutedText, fontWeight: 700, marginBottom: 8 }}>Variants</div>
              {p.variants.map((v) => <div key={v.id} style={{ fontSize: 13, padding: "4px 0" }}>{[v.color, v.size].filter(Boolean).join(" / ")} · {v.stock} stock {v.sku ? `· ${v.sku}` : ""}</div>)}
            </div>
          )}
        </ACard>
      </div>

      <ACard style={{ marginTop: 16 }}>
        <h3 className="font-display" style={{ margin: "0 0 12px", fontSize: 18, fontWeight: 700 }}>Reviews ({reviews.length})</h3>
        {reviews.length === 0 ? <p style={{ color: c.mutedText, margin: 0 }}>No reviews.</p> : reviews.map((r) => (
          <div key={r.id} style={{ padding: "10px 0", borderBottom: `1px solid ${c.divider}` }}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <strong style={{ fontSize: 13.5 }}>{r.user_name}</strong>
              <StarRating rating={r.rating} showCount={false} size={13} />
            </div>
            {r.body && <p style={{ margin: "4px 0 0", fontSize: 13.5, color: c.onSurfaceSecondary }}>{r.body}</p>}
          </div>
        ))}
      </ACard>
    </div>
  );
}
